import React, { useCallback } from 'react';
import {
  Feature,
  FeaturePoints,
  Icon,
  InputSection,
  Label,
  MainDiv,
  Price,
  PriceTag,
  PricingSection,
  SectionDiv,
  UserCard,
  UserCount,
  UserText
} from './styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import { observer } from 'mobx-react-lite';
import CheckboxInput from '../../../input/checkboxInput';
import { ICON_SIZE } from '../../../../global/constants';

/**
 * PlanCard Component
 * @param {Object} props - Component props
 * @param {string} props.label - Plan display name
 * @param {string} props.price - Formatted price label (with currency)
 * @param {string} [props.priceTag] - Price suffix text (e.g., '/ users')
 * @param {number} [props.usersCount] - Optional users count badge
 * @param {boolean} [props.checked] - Whether the plan is selected
 * @param {string[]} props.features - Bullet points shown under the plan
 **/

interface PlanCardProps {
  label: string;
  price: string;
  priceTag?: string;
  usersCount?: number;
  features: string[];
  isSelected?: boolean;
  onCheckboxClick: () => void;
}

const PlanCard: React.FC<PlanCardProps> = ({
  label,
  price,
  priceTag,
  usersCount,
  features,
  isSelected,
  onCheckboxClick
}) => {
  // Memoized renderer for the features list to avoid remounting on unrelated re-renders
  const featureList = useCallback(() => {
    return features.map((text, idx) => (
      <FeaturePoints key={text}>
        <Icon>
          <SVGIcon
            name='correct-sign-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </Icon>
        <Feature>{text}</Feature>
      </FeaturePoints>
    ));
  }, [features]);

  return (
    <>
      <MainDiv role='button' isSelected={isSelected} onClick={onCheckboxClick}>
        <SectionDiv>
          <InputSection>
            <Label>{label}</Label>
            <CheckboxInput checked={isSelected} onChange={onCheckboxClick} />
          </InputSection>
          <InputSection isAlignedBottom={true} className='show-users'>
            <PricingSection>
              <Price>{price}</Price>
              <PriceTag>{priceTag}</PriceTag>
            </PricingSection>
            {/* Render users badge only if count passed */}
            {usersCount !== 0 && (
              <UserCard>
                <UserText>Users :</UserText>
                <UserCount>{usersCount}</UserCount>
              </UserCard>
            )}
          </InputSection>
        </SectionDiv>
        {/* Features list */}
        <SectionDiv>{featureList()}</SectionDiv>
      </MainDiv>
    </>
  );
};

export default observer(PlanCard);
